import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Grid } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import FilterBtn from "../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";

import StatusTabs from "./StatusTabs";
import { columns } from "./columns";
import Filter from "./Filter";
import { rows } from "./data";
import { createFilterHandler } from "../../../../Utils/FilterUtils";

const dataGridCustomStyles = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
    color: "#383E49",
    fontWeight: 500,
  },
  "& .low-stock-row": {
    backgroundColor: "rgba(255, 165, 0, 0.05)",
    "&:hover": {
      backgroundColor: "rgba(255, 165, 0, 0.1) !important",
    },
  },
  "& .out-of-stock-row": {
    backgroundColor: "rgba(255, 0, 0, 0.05)",
    "&:hover": {
      backgroundColor: "rgba(255, 0, 0, 0.1) !important",
    },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "lexend",
    fontSize: "14px",
    fontWeight: 500,
    color: "#667085",
  },
};

const INITIAL_FILTER_STATE = {
  code: "",
  description: "",
  active: "",
  inventoryMin: "",
  inventoryMax: "",
  thresholdValueMin: "",
  thresholdValueMax: "",
  bayLocation: null,
  lastSyncedStart: null,
  lastSyncedEnd: null,
};

const fieldMappings = {
  code: ["CODE", "$eq"],
  description: ["description", "$contains"],
  active: ["active", "$eq"],
  bayLocation: ["bayLocation", "$eq"],
};

const rangeFields = {
  inventory: {
    apiField: "inventory",
    minField: "inventoryMin",
    maxField: "inventoryMax",
  },
  threshold: {
    apiField: "thresholdValue",
    minField: "thresholdValueMin",
    maxField: "thresholdValueMax",
  },
  lastSynced: {
    apiField: "lastSynced",
    minField: "lastSyncedStart",
    maxField: "lastSyncedEnd",
  },
};

export default function Stocks() {
  const [active, setActive] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchStocks = async () => {
    const API_URL = "getPlugins.php";

    try {
      const res = await httpclient.get(API_URL);

      const { data } = res;

      if (data.status === 200) {
        setStocks(data.data);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchStocks();
  }, []);

  const location = useLocation();
  const { type, isFromDashboard } = location?.state ?? {};

  useEffect(() => {
    if (type === "Under Threshold") {
      setActive("Under Threshold");
    }
  }, [type]);

  const handleChangeFilter = (field) => (eventOrValue) => {
    // Handle date pickers (eventOrValue is the Day.js object)
    if (
      field.includes("Date") ||
      field.includes("Start") ||
      field.includes("End")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || null, // Store Day.js object or null
      }));
      return;
    }

    // Handle Autocomplete (eventOrValue is the selected value)
    if (field === "bayLocation") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || "",
      }));
      return;
    }

    // Handle Select dropdown (eventOrValue is the event)
    if (field === "timePeriod") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue.target.value,
      }));
      return;
    }

    // Handle regular text/number inputs (eventOrValue is the event)
    setFilterData((prev) => ({
      ...prev,
      [field]: eventOrValue.target.value,
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setStocks,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: "getPlugins.php",
    fieldMappings,
    rangeFields,
  });

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: field.includes("Min") || field.includes("Max") ? "" : null,
    }));
    setSubmittedData((prev) => {
      const newData = { ...prev };
      delete newData[field];
      return newData;
    });
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (submittedData.active) {
        if (row.active !== submittedData.active) return false;
      } else if (active !== "All" && row.active !== active) {
        return false;
      }

      if (
        submittedData.code &&
        !row.code.toLowerCase().includes(submittedData.code.toLowerCase())
      ) {
        return false;
      }

      if (
        submittedData.description &&
        !row.description
          .toLowerCase()
          .includes(submittedData.description.toLowerCase())
      ) {
        return false;
      }

      if (
        submittedData.inventoryMin &&
        row.inventory < Number(submittedData.inventoryMin)
      ) {
        return false;
      }
      if (
        submittedData.inventoryMax &&
        row.inventory > Number(submittedData.inventoryMax)
      ) {
        return false;
      }

      if (
        submittedData.thresholdValueMin &&
        row.thresholdValue < Number(submittedData.thresholdValueMin)
      ) {
        return false;
      }
      if (
        submittedData.thresholdValueMax &&
        row.thresholdValue > Number(submittedData.thresholdValueMax)
      ) {
        return false;
      }

      if (
        submittedData.bayLocation &&
        row.bayLocation !== submittedData.bayLocation
      ) {
        return false;
      }

      const lastSynced = new Date(row.lastSynced);

      if (
        (submittedData.lastSyncedStart &&
          lastSynced < submittedData.lastSyncedStart.startOf("day").toDate()) ||
        (submittedData.lastSyncedEnd &&
          lastSynced > submittedData.lastSyncedEnd.startOf("day").toDate())
      ) {
        return false;
      }

      return true;
    });
  }, [active, submittedData]);

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      {isFromDashboard && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Box>
      )}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <StatusTabs active={active} setActive={setActive} />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Grid>
          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />
        </Grid>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getRowClassName={(params) => {
            if (params.row.inventory === 0) return "out-of-stock-row";
            if (params.row.inventory < params.row.thresholdValue)
              return "low-stock-row";
            return "";
          }}
          sx={dataGridCustomStyles}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
              },
            },
          }}
        />
      </Box>
    </Box>
  );
}
